/**
 * Firestore bindings for /users/{uid}/fiches/{ficheId}.
 *
 * A "fiche" is a user's saved analysis of a property. It snapshots the
 * pipeline output (geocode + parcel + zone + règlement) at creation time
 * and carries user-editable metadata (title, status, agent, existant...).
 *
 * Règlements are denormalized into the fiche rather than referenced by ID
 * so historical fiches stay stable even when a commune revises its PLU.
 */

import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  type Unsubscribe,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import type {
  GeocodeResult,
  CadastreParcel,
  PluZone,
  Reglement,
} from "./types";

export type FicheType = "Mandat" | "Acheteur";
export type FicheStatus = "brouillon" | "envoyé" | "signé";

export type ExistingProperty = {
  type: "Maison" | "Immeuble" | "Terrain";
  surface_habitable_m2: number | null;
  niveaux: string | null; // "R+1", "R+2+combles", etc.
  emprise_m2: number | null;
  hauteur_m: number | null;
  year_built: number | null;
  notes: string;
  // Origine des champs auto-remplis. Absent = saisie agent (comportement
  // historique). "bdtopo" = pré-rempli depuis la couche IGN BD TOPO batiment
  // au moment du `createFiche`. Utilisé côté UI pour afficher un petit tag
  // "BD TOPO" sous la valeur, invitant l'agent à vérifier.
  sources?: {
    emprise_m2?: "bdtopo";
    hauteur_m?: "bdtopo";
  };
};

export type FicheDoc = {
  id: string;
  userId: string;
  createdAt: Timestamp | null; // null while pending server timestamp
  updatedAt: Timestamp | null;

  // User-editable
  title: string;
  type: FicheType;
  status: FicheStatus;
  agent: string; // initials

  // Snapshot from the pipeline — frozen at creation
  geocode: GeocodeResult;
  parcel: CadastreParcel;
  zone: PluZone;
  reglement: Reglement;

  // User-provided existant (step 2 of Nouvelle)
  existing: ExistingProperty | null;

  // Flags d'inclusion PDF pour les sous-sections de l'onglet Fiche.
  // Écrit par la modale d'export PDF. Absent = tout inclus (default
  // true) pour les fiches créées avant cette feature.
  pdfIncludeFiche?: {
    bien?: boolean;
    reglement?: boolean;
    risques?: boolean;
    dvf?: boolean;
  };

  // ─── Organisation (PR #1 folders) ────────────────────────────────────
  // IDs of /users/{uid}/folders this fiche belongs to. A fiche can belong
  // to arbitrarily many folders (tag-like semantics). Empty / missing on
  // legacy fiches — hydrateFiche() normalizes to [].
  folderIds: string[];
  // Starred fiches float to the top of the sidebar "Épinglées" section.
  starred: boolean;
  // Archived fiches are hidden from the default lists but preserved for
  // history (soft-delete). Toggled, never auto-set.
  archived: boolean;
};

// ─── Paths ───────────────────────────────────────────────────────────────
function fichesCol(uid: string) {
  return collection(db, "users", uid, "fiches");
}
function ficheRef(uid: string, ficheId: string) {
  return doc(db, "users", uid, "fiches", ficheId);
}

// ─── ID generation — human-readable "F-YYMMXX" slug ──────────────────────
// Uses a random 3-char suffix (not guaranteed unique, but collision probability
// at <10k/month is negligible; we check with a getDoc pre-write).
/**
 * Generate a fresh fiche id. Callers usually let `createFiche` mint it itself,
 * but the manual-PLU-upload flow needs the id ahead of time so the upload's
 * Storage path (`users/{uid}/fiches/{id}/pluUploads/...`) lines up with the
 * fiche eventually created from the same run.
 */
export function newFicheId(): string {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const suffix = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `F-${yy}${mm}${suffix}`;
}

// ─── Create ──────────────────────────────────────────────────────────────
export type CreateFicheInput = {
  geocode: GeocodeResult;
  parcel: CadastreParcel;
  zone: PluZone;
  reglement: Reglement;
  existing: ExistingProperty | null;
  type?: FicheType;
  agent?: string;
  /** Pre-allocated id (used by the manual-PLU-upload flow so the Storage
   *  path of the uploaded PDFs matches the eventual fiche). When omitted
   *  we mint a fresh id. */
  id?: string;
};

export async function createFiche(input: CreateFicheInput): Promise<FicheDoc> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");

  const id = input.id ?? newFicheId();
  const title = defaultTitle(input);

  const payload: Omit<FicheDoc, "createdAt" | "updatedAt" | "parcel"> & {
    createdAt: unknown;
    updatedAt: unknown;
    parcel: Omit<CadastreParcel, "geometry"> & { geometry: string };
  } = {
    id,
    userId: user.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    title,
    type: input.type ?? "Mandat",
    status: "brouillon",
    agent: input.agent ?? initialsFor(user.displayName ?? user.email ?? ""),
    geocode: input.geocode,
    parcel: serializeParcel(input.parcel),
    zone: input.zone,
    reglement: input.reglement,
    existing: input.existing,
    folderIds: [],
    starred: false,
    archived: false,
  };

  await setDoc(ficheRef(user.uid, id), payload);

  // Return a synthetic view — Firestore will materialize createdAt/updatedAt
  // shortly after; the UI should subscribe via subscribeFiche to see them.
  // Restore the original parcel (with geometry as object) so callers don't see
  // a stringified blob right after create.
  return {
    ...payload,
    parcel: input.parcel,
    createdAt: null,
    updatedAt: null,
  } as FicheDoc;
}

// ─── Firestore <-> runtime conversion for CadastreParcel.geometry ──────────
// Firestore documents cannot contain nested arrays directly, but GeoJSON
// Polygon/MultiPolygon coordinates are triply (or deeper) nested arrays. We
// therefore store `geometry` as a JSON string and parse it back on read.
function serializeParcel(
  p: CadastreParcel,
): Omit<CadastreParcel, "geometry"> & { geometry: string } {
  return { ...p, geometry: JSON.stringify(p.geometry ?? null) };
}

function hydrateFiche(raw: unknown): FicheDoc {
  const f = raw as FicheDoc & {
    parcel?: CadastreParcel & { geometry?: unknown };
  };
  if (f?.parcel && typeof f.parcel.geometry === "string") {
    try {
      f.parcel.geometry = JSON.parse(f.parcel.geometry);
    } catch {
      // Leave as-is; consumers that don't need geometry will still work.
    }
  }
  // Organisation fields — default-fill on legacy fiches written before PR #1.
  f.folderIds = Array.isArray(f.folderIds) ? f.folderIds : [];
  f.starred = f.starred === true;
  f.archived = f.archived === true;
  return f as FicheDoc;
}

function defaultTitle(input: CreateFicheInput): string {
  const existingType = input.existing?.type ?? "Bien";
  const surface = input.existing?.surface_habitable_m2;
  const prefix = surface ? `${existingType} ${surface} m²` : existingType;
  return `${prefix} — ${input.geocode.label}`;
}

function initialsFor(nameOrEmail: string): string {
  const clean = nameOrEmail.split("@")[0];
  const parts = clean.replace(/[._-]+/g, " ").trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) ?? "??").toUpperCase();
}

// ─── Read (one-shot) ─────────────────────────────────────────────────────
export async function getFiche(ficheId: string): Promise<FicheDoc | null> {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(ficheRef(user.uid, ficheId));
  if (!snap.exists()) return null;
  return hydrateFiche(snap.data());
}

// ─── Read (live subscription) ────────────────────────────────────────────
export function subscribeFiche(
  ficheId: string,
  cb: (fiche: FicheDoc | null) => void,
): Unsubscribe {
  const user = auth.currentUser;
  if (!user) {
    cb(null);
    return () => {};
  }
  return onSnapshot(ficheRef(user.uid, ficheId), (snap) => {
    cb(snap.exists() ? hydrateFiche(snap.data()) : null);
  });
}

// ─── Update parcel (used when the user picks an alternative parcel) ──────
// Swapping parcels doesn't invalidate zone/règlement — those are keyed on
// the geocoded (lat,lng) and commune, not on the parcel itself — so we only
// touch the `parcel` field here.
export async function updateFicheParcel(
  ficheId: string,
  parcel: CadastreParcel,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await updateDoc(ficheRef(user.uid, ficheId), {
    parcel: serializeParcel(parcel),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Atomically updates both the parcel and the `existing` snapshot — used when
 * the user picks a different parcel via the picker AND we re-ran BD TOPO on
 * the new geometry. Single Firestore write keeps the two correlated values
 * in sync (no transient state where parcel = new but existing = old footprint).
 */
export async function updateFicheParcelAndExisting(
  ficheId: string,
  parcel: CadastreParcel,
  existing: ExistingProperty,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await updateDoc(ficheRef(user.uid, ficheId), {
    parcel: serializeParcel(parcel),
    existing,
    updatedAt: serverTimestamp(),
  });
}

// ─── Delete ──────────────────────────────────────────────────────────────
// Hard delete. Firestore rules already restrict deletes to the fiche owner,
// so we don't re-check ownership here — the server will reject if the doc
// doesn't belong to the caller.
export async function deleteFiche(ficheId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await deleteDoc(ficheRef(user.uid, ficheId));
}

// ─── List user's fiches (for the Dashboard) ──────────────────────────────
export function subscribeMyFiches(cb: (fiches: FicheDoc[]) => void): Unsubscribe {
  const user = auth.currentUser;
  if (!user) {
    cb([]);
    return () => {};
  }
  const q = query(fichesCol(user.uid), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => hydrateFiche(d.data())));
  });
}

// ─── Organisation helpers ────────────────────────────────────────────────
// Toggle the starred flag. Starred fiches float to the sidebar top.
export async function setFicheStarred(
  ficheId: string,
  starred: boolean,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await updateDoc(ficheRef(user.uid, ficheId), {
    starred,
    updatedAt: serverTimestamp(),
  });
}

// Toggle the archived flag. Archived fiches are excluded from default lists.
export async function setFicheArchived(
  ficheId: string,
  archived: boolean,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await updateDoc(ficheRef(user.uid, ficheId), {
    archived,
    updatedAt: serverTimestamp(),
  });
}

// Add the fiche to a folder (idempotent — arrayUnion dedupes).
export async function addFicheToFolder(
  ficheId: string,
  folderId: string,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await updateDoc(ficheRef(user.uid, ficheId), {
    folderIds: arrayUnion(folderId),
    updatedAt: serverTimestamp(),
  });
}

// Remove the fiche from a folder (no-op if not a member).
export async function removeFicheFromFolder(
  ficheId: string,
  folderId: string,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await updateDoc(ficheRef(user.uid, ficheId), {
    folderIds: arrayRemove(folderId),
    updatedAt: serverTimestamp(),
  });
}

// Replace the full folder membership. Used by the bulk-edit UI on
// "Mes fiches" when the user reassigns a selection at once.
export async function setFicheFolders(
  ficheId: string,
  folderIds: string[],
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  // Dedupe to avoid accidental duplicates from chip UIs.
  const unique = Array.from(new Set(folderIds));
  await updateDoc(ficheRef(user.uid, ficheId), {
    folderIds: unique,
    updatedAt: serverTimestamp(),
  });
}
